import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ApiError, useAcceptInvitation, useAuthStore } from '@wordpuppi/shared';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ThemeToggle } from '@/components/ThemeToggle';

export const Route = createFileRoute('/invite/$token')({
  component: InvitePage,
});

/**
 * Where a pending invite token is stashed while an unauthenticated invitee logs
 * in or signs up. `login.tsx` reads this after auth and routes back here so the
 * accept can complete (survives the register → verify-email → login detour that
 * a search param wouldn't). AB#451.
 */
export const PENDING_INVITE_KEY = 'wpp-pending-invite';

function InvitePage() {
  const { token } = Route.useParams();
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const accept = useAcceptInvitation();
  const [error, setError] = useState<string | null>(null);

  // Stash the token so login/register can bring the invitee back here (AB#451).
  useEffect(() => {
    if (!isAuthenticated && token) {
      localStorage.setItem(PENDING_INVITE_KEY, token);
    }
  }, [isAuthenticated, token]);

  const finish = () => {
    localStorage.removeItem(PENDING_INVITE_KEY);
    navigate({ to: '/account/team' });
  };

  const handleAccept = async () => {
    setError(null);
    try {
      await accept.mutateAsync({ token });
      toast.success('Invitation accepted');
      finish();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          // already_member — idempotent success: just send them in.
          toast.success("You're already a member of this team");
          finish();
          return;
        }
        if (err.status === 403) {
          setError(
            'This invite was sent to a different email address. Log in with that address to accept it.',
          );
        } else if (err.status === 404) {
          setError('This invitation link is invalid or has expired.');
        } else if (err.status === 402) {
          setError('This team is full — ask the owner to free up or add a seat, then try again.');
        } else {
          setError(err.message);
        }
      } else {
        setError(err instanceof Error ? err.message : 'Could not accept the invitation.');
      }
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <Card className="w-full max-w-[400px]">
        <CardHeader>
          <CardTitle className="text-center text-2xl">You've been invited</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
              {error}
            </div>
          )}

          {isAuthenticated ? (
            <>
              <p className="text-sm text-muted-foreground text-center">
                Accept this invitation to join the team on WordPuppi.
              </p>
              <Button className="w-full" disabled={accept.isPending} onClick={handleAccept}>
                {accept.isPending ? 'Accepting...' : 'Accept invitation'}
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground text-center">
                You've been invited to join a team on WordPuppi. Log in or create an account to
                accept — we'll bring you right back here.
              </p>
              <Button asChild className="w-full">
                <Link to="/login">Log in</Link>
              </Button>
              <Button asChild variant="outline" className="w-full">
                <Link to="/register">Create account</Link>
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
